import React, {FC, useEffect, useRef} from 'react';
import {createPortal} from 'react-dom';
import {Formik, Form, Field, ErrorMessage, FormikProps} from 'formik';
import {useTranslation} from 'react-i18next';
import * as Yup from 'yup';
import {toast} from 'react-toastify';
import {applyCooperate} from 'apis/users';
import wechatConcat01 from 'assets/contact/wechat-concat-01.jpg';
import wechatConcat02 from 'assets/contact/wechat-concat-02.jpg';

import * as S from './Styles';

interface CooperationFormData {
  companyName: string;
  contactPerson: string;
  cooperationDetails: string;
  phone: string;
  wechat: string;
}

interface CooperationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const initialValues: CooperationFormData = {
  companyName: '',
  contactPerson: '',
  cooperationDetails: '',
  phone: '',
  wechat: '',
};

const CooperationModal: FC<CooperationModalProps> = ({isOpen, onClose}) => {
  const formikRef = useRef<FormikProps<CooperationFormData>>(null);
  const {t} = useTranslation('common');

  // 联系电话与企业名称必填（2026-09-18 业务要求企业名称也必填）。
  // schema 依赖 t()，须在组件内构建（i18n）
  const validationSchema = Yup.object({
    companyName: Yup.string().required(t('cooperation.companyRequired')),
    contactPerson: Yup.string(),
    cooperationDetails: Yup.string(),
    phone: Yup.string().required(t('cooperation.phoneRequired')),
    wechat: Yup.string(),
  });

  // 表单重置函数
  const resetForm = () => {
    if (formikRef.current) {
      formikRef.current.resetForm();
      formikRef.current.setTouched({}); // 清除touched状态
      formikRef.current.setErrors({}); // 清除错误状态
    }
  };
  // 关闭Modal时重置表单
  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (values: CooperationFormData) => {
    try {
      const response = await applyCooperate(values);
      if (response.code === '0') {
        toast.success(response.message || t('cooperation.submitSuccess'));
        handleClose();
      } else {
        toast.error(response.message || t('cooperation.submitFailed'));
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('cooperation.submitFailed');
      toast.error(errorMessage);
    } finally {
      //   setSubmitting(false);
    }
  };

  // 处理键盘事件和body滚动
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    // 禁用body滚动
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';

    // 添加键盘事件监听
    document.addEventListener('keydown', handleEscape);

    return () => {
      // 恢复body滚动
      document.body.style.overflow = originalStyle;
      // 移除键盘事件监听
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const modalContent = (
    <S.Backdrop onClick={onClose}>
      <S.ModalContainer onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <S.Header>
          <S.Title>{t('cooperation.title')}</S.Title>
          <S.CloseButton onClick={onClose}>×</S.CloseButton>
        </S.Header>

        <S.Content>
          <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
            {({isSubmitting}) => (
              <Form>
                {/* 顺序（2026-09-18 调整）：联系电话提前至首位，企业名称居次 */}
                <S.FormGroup>
                  <S.Label htmlFor="phone" required>
                    {t('cooperation.phoneLabel')}
                  </S.Label>
                  <Field
                    as={S.Input}
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder={t('cooperation.phonePlaceholder')}
                  />
                  <ErrorMessage name="phone" component={S.ErrorText} />
                </S.FormGroup>

                <S.FormGroup>
                  <S.Label htmlFor="companyName" required>
                    {t('cooperation.companyLabel')}
                  </S.Label>
                  <Field
                    as={S.Input}
                    id="companyName"
                    name="companyName"
                    type="text"
                    placeholder={t('cooperation.companyPlaceholder')}
                  />
                  <ErrorMessage name="companyName" component={S.ErrorText} />
                </S.FormGroup>

                <S.FormGroup>
                  <S.Label htmlFor="wechat">{t('cooperation.wechatLabel')}</S.Label>
                  <Field
                    as={S.Input}
                    id="wechat"
                    name="wechat"
                    type="text"
                    placeholder={t('cooperation.wechatPlaceholder')}
                  />
                  <ErrorMessage name="wechat" component={S.ErrorText} />
                </S.FormGroup>

                <S.FormGroup>
                  <S.Label htmlFor="contactPerson">{t('cooperation.contactLabel')}</S.Label>
                  <Field
                    as={S.Input}
                    id="contactPerson"
                    name="contactPerson"
                    type="text"
                    placeholder={t('cooperation.contactPlaceholder')}
                  />
                  <ErrorMessage name="contactPerson" component={S.ErrorText} />
                </S.FormGroup>

                <S.FormGroup>
                  <S.Label htmlFor="cooperationDetails">{t('cooperation.detailsLabel')}</S.Label>
                  <Field
                    as={S.TextArea}
                    id="cooperationDetails"
                    name="cooperationDetails"
                    rows={4}
                    placeholder={t('cooperation.detailsPlaceholder')}
                  />
                  <ErrorMessage name="cooperationDetails" component={S.ErrorText} />
                </S.FormGroup>

                <S.ContactSection>
                  <S.ContactTitle>{t('cooperation.contactUs')}</S.ContactTitle>
                  <S.QRCodeContainer>
                    <S.QRCodeWrapper>
                      <S.QRCodeImage
                        src={wechatConcat01}
                        alt={t('cooperation.qrAltWechat')}
                        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                          // 如果图片加载失败，显示占位符
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </S.QRCodeWrapper>
                    <S.QRCodeWrapper>
                      <S.QRCodeImage
                        src={wechatConcat02}
                        alt={t('cooperation.qrAltQq')}
                        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </S.QRCodeWrapper>
                  </S.QRCodeContainer>
                </S.ContactSection>

                <S.SubmitButton type="submit" disabled={isSubmitting}>
                  {isSubmitting ? t('cooperation.submitting') : t('cooperation.submit')}
                </S.SubmitButton>
              </Form>
            )}
          </Formik>
        </S.Content>
      </S.ModalContainer>
    </S.Backdrop>
  );

  // 使用 React Portal 将模态框渲染到 document.body
  return createPortal(modalContent, document.body);
};

export default CooperationModal;
